import React, { useState } from "react";
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { Link } from "react-router-dom";
import { FaPaypal, FaShieldAlt } from "react-icons/fa";

const PaypalCheckout = () => {
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();
  const [amount, setAmount] = useState("15.00");
  const [currency, setCurrency] = useState(options.currency || "USD");

  const tierPresets = [
    { label: "Coffee", value: "5.00" },
    { label: "Feature Backer", value: "15.00" },
    { label: "Sponsor Sprint", value: "50.00" },
    { label: "Partner Patron", value: "120.00" },
  ];

  const onCurrencyChange = (event) => {
    const nextCurrency = event.target.value;
    setCurrency(nextCurrency);
    dispatch({
      type: "resetOptions",
      value: {
        ...options,
        currency: nextCurrency,
      },
    });
  };

  return (
    <div className="w-full max-w-xl rounded-3xl border border-slate-200/80 bg-white p-5 sm:p-9 text-slate-900 shadow-xs">
      <div className="flex items-center gap-3.5 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl border border-blue-100 shrink-0">
          <FaPaypal />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">International PayPal Gateway</h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium">Direct Global Developer Sponsorship</p>
        </div>
      </div>

      <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
        Support open-source development from anywhere globally with standard credit/debit cards or your PayPal balance.
      </p>

      {/* Preset tiers */}
      <div className="mt-6">
        <label className="block text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600 mb-2.5">
          Select Tier
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {tierPresets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => setAmount(preset.value)}
              className={`py-2.5 px-3 rounded-2xl text-sm font-bold transition border cursor-pointer ${
                amount === preset.value
                  ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                  : "bg-[#FAF9F6] text-slate-700 border-slate-200 hover:border-slate-300 active:bg-slate-100"
              }`}
            >
              <div className="text-sm font-extrabold">${preset.value}</div>
              <div className="text-[11px] font-medium opacity-80 mt-0.5">{preset.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600">Contribution Amount</label>
          <input
            type="number"
            min="1.00"
            step="1.00"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-[#FAF9F6] px-4 py-3 text-slate-900 font-extrabold text-base sm:text-lg outline-hidden focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600">Currency</label>
          <select
            value={currency}
            onChange={onCurrencyChange}
            className="w-full rounded-2xl border border-slate-200 bg-[#FAF9F6] px-4 py-3 text-slate-900 font-bold text-sm sm:text-base outline-hidden focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          >
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
            <option value="AUD">AUD ($)</option>
            <option value="CAD">CAD ($)</option>
          </select>
        </div>
      </div>

      <div className="mt-6 min-h-[56px] pt-4 border-t border-slate-100">
        {isPending ? (
          <div className="p-4 text-center text-xs text-slate-500 font-medium animate-pulse">
            Connecting to secure PayPal payment services...
          </div>
        ) : (
          <div className="space-y-3">
            <PayPalButtons
              style={{ layout: "vertical", color: "blue", shape: "pill", label: "pay", height: 46 }}
              createOrder={(data, actions) =>
                actions.order.create({
                  purchase_units: [
                    {
                      description: "Developer & Open-Source Project Sponsorship",
                      amount: { value: amount },
                    },
                  ],
                })
              }
              onApprove={(data, actions) =>
                actions.order.capture().then((details) => {
                  alert(`Sponsorship confirmed! Thank you, ${details.payer.name.given_name}, for supporting independent open source.`);
                })
              }
              onError={(err) => {
                console.error("PayPal checkout error:", err);
              }}
              onCancel={() => {
                console.log("PayPal checkout cancelled by user");
              }}
            />
          </div>
        )}
      </div>

      <div className="mt-4 text-center">
        <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1.5 font-medium">
          <FaShieldAlt className="text-emerald-500" />
          Processed with 256-bit SSL secure checkout by PayPal
        </p>
      </div>
    </div>
  );
};

const Paypal = () => {
  const rawClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || "AUZz__LDXxF9lqYhCydAKrhSHHgIGmtYomOsTOvcFrQIS2o0l5c9JdAk2Kc4PN6yZ0PkmijhUw1DKL72";
  const clientId = rawClientId ? rawClientId.replace(/["']/g, "").trim() : "test";
  const [scriptLoadError, setScriptLoadError] = useState(null);

  return (
    <PayPalScriptProvider
      options={{
        clientId: clientId,
        currency: "USD",
        intent: "capture",
      }}
      onError={(err) => {
        console.error("PayPal SDK script failed to load:", err);
        setScriptLoadError("Unable to load PayPal checkout script. Please check your internet connection or verify the PayPal Client ID.");
      }}
    >
      <section className="min-h-screen bg-[#FAF9F6] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-xl">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition"
            >
              ← Back to Sponsorship Hub
            </Link>
          </div>
          {scriptLoadError && (
            <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
              ⚠️ {scriptLoadError}
            </div>
          )}
          <PaypalCheckout />
        </div>
      </section>
    </PayPalScriptProvider>
  );
};

export default Paypal;
